import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { toast } from "react-toastify";
import axiosInstance from "../../HelperAxios/axios";
import StrinLocalized from "../../Language/language.lang";
import { canCreateTimeEntry } from "../../Utils/timeEntryAccess";
import { contactLabel, formatElapsed, userLabel } from "../../Utils/timeEntryUtils";

const TimeEntryTimer = ({
  compact = false,
  defaultContact = null,
  onChanged,
}) => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contact, setContact] = useState(defaultContact);
  const [project, setProject] = useState(null);
  const [description, setDescription] = useState("");
  const [, setTick] = useState(0);

  const canRun = canCreateTimeEntry();

  const loadRunning = async () => {
    if (!canRun) return null;
    try {
      const res = await axiosInstance.get("/time-entries/running");
      const current = res?.data?.id ? res.data : null;
      setRunning(current);
      return current;
    } catch {
      setRunning(null);
      return null;
    }
  };

  useEffect(() => {
    loadRunning();
  }, []);

  useEffect(() => {
    if (!running?.startedAt) return undefined;
    const id = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(id);
  }, [running?.startedAt]);

  useEffect(() => {
    if (!open) return;
    axiosInstance
      .get("contact/select-list")
      .then((res) => setContacts(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setContacts([]));
    axiosInstance
      .get("project/select-list")
      .then((res) => setProjects(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setProjects([]));
  }, [open]);

  const contactOptions = useMemo(
    () =>
      (contacts || []).map((item) => ({
        value: item.id,
        label: contactLabel(item),
      })),
    [contacts]
  );

  const projectOptions = useMemo(
    () =>
      (projects || []).map((item) => ({
        value: item.id,
        label: item.name || `#${item.id}`,
      })),
    [projects]
  );

  const syncFormFromRunning = (current) => {
    if (!current) {
      setContact(defaultContact);
      setProject(null);
      setDescription("");
      return;
    }
    setContact({
      value: current.contact?.id,
      label: contactLabel(current.contact),
    });
    setProject(
      current.project
        ? { value: current.project.id, label: current.project.name }
        : null
    );
    setDescription(current.description || "");
  };

  const lastDescriptionForContact = async (contactId) => {
    try {
      const res = await axiosInstance.get(
        `/time-entries?contactId=${contactId}&limit=10&page=1`
      );
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const recorded = list.find(
        (item) =>
          item?.status !== "running" &&
          item?.status !== "discarded" &&
          String(item?.description || "").trim()
      );
      return recorded?.description || "";
    } catch {
      return "";
    }
  };

  const handleOpen = async () => {
    setLoading(true);
    const current = await loadRunning();
    syncFormFromRunning(current);
    if (!current && defaultContact?.value) {
      const lastDescription = await lastDescriptionForContact(defaultContact.value);
      if (lastDescription) setDescription(lastDescription);
    }
    setOpen(true);
    setLoading(false);
  };

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
  };

  const apiError = (err) => {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      StrinLocalized.SomethingWentWrong ||
      "Something went wrong";
    toast.error(Array.isArray(message) ? message[0] : message);
  };

  const handleStart = async () => {
    if (!contact?.value) {
      toast.error(StrinLocalized.ContactRequired || "Select a contact");
      return;
    }
    setSaving(true);
    try {
      const res = await axiosInstance.post("/time-entries/start", {
        contactId: contact.value,
        description: description.trim() || undefined,
        projectId: project?.value || undefined,
      });
      setRunning(res.data);
      toast.success(StrinLocalized.TimerStarted || "Timer started");
      onChanged?.(res.data);
      setOpen(false);
    } catch (err) {
      apiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleStop = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.post("/time-entries/stop", {
        description: description.trim() || undefined,
      });
      setRunning(null);
      toast.success(StrinLocalized.TimerStopped || "Timer stopped");
      onChanged?.(res.data);
      setOpen(false);
    } catch (err) {
      apiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.post("/time-entries/discard");
      setRunning(null);
      toast.success(StrinLocalized.TimerDiscarded || "Timer discarded");
      onChanged?.(res.data);
      setOpen(false);
    } catch (err) {
      apiError(err);
    } finally {
      setSaving(false);
    }
  };

  if (!canRun) return null;

  const elapsed = running ? formatElapsed(running.startedAt) : "00:00:00";
  const runningOnOtherContact =
    Boolean(running) &&
    defaultContact?.value &&
    Number(running.contact?.id) !== Number(defaultContact.value);

  return (
    <>
      {compact ? (
        <Tooltip
          title={
            running
              ? `${StrinLocalized.TimerRunning || "Timer running"} ${elapsed}`
              : StrinLocalized.StartTimer || "Start timer"
          }
        >
          <Chip
            icon={<TimerOutlinedIcon />}
            label={running ? elapsed : StrinLocalized.Timer || "Timer"}
            color={running ? "success" : "default"}
            variant={running ? "filled" : "outlined"}
            onClick={handleOpen}
            sx={{ cursor: "pointer" }}
          />
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          startIcon={<TimerOutlinedIcon />}
          onClick={handleOpen}
        >
          {running
            ? `${StrinLocalized.TimerRunning || "Timer running"} ${elapsed}`
            : StrinLocalized.StartTimer || "Start timer"}
        </Button>
      )}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {running
            ? StrinLocalized.RunningTimer || "Running timer"
            : StrinLocalized.StartTimer || "Start timer"}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              {running && (
                <>
                  <Chip
                    color="success"
                    label={`${StrinLocalized.Elapsed || "Elapsed"}: ${elapsed}`}
                  />
                  <Typography variant="body2">
                    {StrinLocalized.RecordedBy || StrinLocalized.User}:{" "}
                    {userLabel(running.user)}
                  </Typography>
                </>
              )}
              {runningOnOtherContact && (
                <Chip
                  color="warning"
                  label={
                    StrinLocalized.TimerRunningOnOtherContact ||
                    "A timer is already running on another contact"
                  }
                />
              )}
              <Autocomplete
                options={contactOptions}
                value={contact}
                onChange={(_, value) => setContact(value)}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                disabled={Boolean(running) || Boolean(defaultContact?.value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={StrinLocalized.Contact || "Contact"}
                  />
                )}
              />
              <Autocomplete
                options={projectOptions}
                value={project}
                onChange={(_, value) => setProject(value)}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                disabled={Boolean(running)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${StrinLocalized.Project || "Project"} (${
                      StrinLocalized.Optional || "optional"
                    })`}
                  />
                )}
              />
              <TextField
                multiline
                minRows={3}
                label={StrinLocalized.Description || "Description"}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={saving}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            {StrinLocalized.Cancel || "Cancel"}
          </Button>
          {running ? (
            <>
              <Button color="inherit" onClick={handleDiscard} disabled={saving}>
                {StrinLocalized.DiscardTimer || "Discard"}
              </Button>
              <Button variant="contained" onClick={handleStop} disabled={saving}>
                {saving
                  ? StrinLocalized.Sending || "Sending..."
                  : StrinLocalized.StopTimer || "Stop timer"}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={handleStart}
              disabled={saving || !contact?.value}
            >
              {saving
                ? StrinLocalized.Sending || "Sending..."
                : StrinLocalized.StartTimer || "Start timer"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

TimeEntryTimer.propTypes = {
  compact: PropTypes.bool,
  defaultContact: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
  }),
  onChanged: PropTypes.func,
};

export default TimeEntryTimer;
